// DelegateInbox — "Delegated to me", grouped for the person covering:
//   Awaiting your acceptance / Upcoming / Active / Completed & cancelled.
// Each card: who asked, what kind of absence, the dates, status, the handover
// message, and Accept / Decline where the delegate still has to answer.
//
// Four states, never collapsed: loading, error (retry — a failed load must not
// read as "no one needs you"), empty, data.
import React from 'react';
import { Check, XCircle, CalendarClock, Hourglass } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { DataState } from '@/features/shared/components/ui/DataState';
import { DelegationNotes } from './DelegationNotes';
import {
  delegationStatusLabel,
  firstName,
  groupDelegateInbox,
  isCoverageOnly,
  leaveTypeLabel,
  linkedLeaveStatus,
  type HandoverDelegation,
} from './handover';

export function fmtWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

export function personName(name?: string | null): string {
  return (name ?? '').trim() || 'Colleague';
}

/** What the delegate is being asked to do, in words. */
export function delegationScopeText(d: HandoverDelegation, capLabels: (keys: string[]) => string): string {
  if (isCoverageOnly(d)) return 'Coverage only — no approval rights are delegated';
  return d.scopeSummary || capLabels(d.capabilities);
}

export function statusVariant(d: HandoverDelegation): 'success' | 'warning' | 'danger' | 'secondary' {
  const s = String(d.status ?? (d.active ? 'active' : ''));
  if (s === 'active') return 'success';
  if (s === 'pending_acceptance' || s === 'pending_approval') return 'warning';
  if (s === 'declined' || s === 'rejected') return 'danger';
  return 'secondary';
}

function kindLabel(d: HandoverDelegation): string {
  return d.leaveRequestId || d.origin === 'leave' ? leaveTypeLabel(d) : 'Delegated authority';
}

export interface DelegateCardProps {
  d: HandoverDelegation;
  capLabels: (keys: string[]) => string;
  deciding?: boolean;
  focused?: boolean;
  onRespond?: (d: HandoverDelegation, accept: boolean) => void;
  onOpen?: (d: HandoverDelegation) => void;
}

export const DelegateCard: React.FC<DelegateCardProps> = ({ d, capLabels, deciding, focused, onRespond, onOpen }) => {
  const leave = linkedLeaveStatus(d);
  const actionable = d.status === 'pending_acceptance';
  return (
    <div
      id={`delegation-${d.id}`}
      data-testid="delegate-card"
      className={`rounded-[12px] border p-4 space-y-2 ${focused ? 'border-warning ring-2 ring-warning' : 'border-border'}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-charcoal font-medium">{personName(d.delegatorName)}</div>
          <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5">
            <span className="inline-flex items-center gap-1"><CalendarClock className="w-3 h-3" aria-hidden />{kindLabel(d)}</span>
            <span>{fmtWhen(d.startsAt)} → {fmtWhen(d.endsAt)}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {leave === 'pending' && (
            <Badge variant="warning" data-testid="leave-pending-badge">
              <Hourglass className="w-3 h-3" aria-hidden /> Leave pending approval
            </Badge>
          )}
          <Badge variant={statusVariant(d)}>{delegationStatusLabel(d)}</Badge>
        </div>
      </div>
      <div className="text-sm text-muted-foreground">{delegationScopeText(d, capLabels)}</div>
      <DelegationNotes delegation={d} viewer="delegate" />
      {d.status === 'declined' && d.declineReason && (
        <div className="text-xs text-muted-foreground">You declined: {d.declineReason}</div>
      )}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        {actionable && onRespond && (
          <>
            <Button size="sm" disabled={deciding} onClick={() => onRespond(d, true)}>
              <Check className="w-4 h-4 mr-1" />Accept
            </Button>
            <Button size="sm" variant="outline" disabled={deciding} onClick={() => onRespond(d, false)}>
              <XCircle className="w-4 h-4 mr-1" />Decline
            </Button>
          </>
        )}
        {onOpen && (
          <Button size="sm" variant="ghost" onClick={() => onOpen(d)}>
            Details
          </Button>
        )}
      </div>
    </div>
  );
};

export interface DelegateInboxProps {
  rows: HandoverDelegation[];
  loading: boolean;
  /** Load error message (null when the last load succeeded). */
  error: string | null;
  onRetry: () => void;
  capLabels: (keys: string[]) => string;
  decidingId?: string | null;
  focusedId?: string | null;
  onRespond: (d: HandoverDelegation, accept: boolean) => void;
  onOpen?: (d: HandoverDelegation) => void;
  now?: number;
}

export const DelegateInbox: React.FC<DelegateInboxProps> = ({
  rows, loading, error, onRetry, capLabels, decidingId, focusedId, onRespond, onOpen, now,
}) => {
  const body = (() => {
    if (loading) return <DataState variant="loading" compact message="Loading what's been delegated to you…" />;
    if (error) {
      return <DataState variant="error" title="Couldn't load your delegations" message={error} onRetry={onRetry} />;
    }
    if (rows.length === 0) return <DataState variant="empty" compact message="No one has delegated to you." />;
    const g = groupDelegateInbox(rows, now);
    const groups: { key: keyof typeof g; title: string }[] = [
      { key: 'awaiting', title: 'Awaiting your acceptance' },
      { key: 'upcoming', title: 'Upcoming' },
      { key: 'active', title: 'Active' },
      { key: 'past', title: 'Completed & cancelled' },
    ];
    return groups
      .filter(({ key }) => g[key].length > 0)
      .map(({ key, title }) => (
        <section key={key} aria-label={title} data-testid={`inbox-group-${key}`} className="space-y-2">
          <h3 className={`text-sm font-medium ${key === 'awaiting' ? 'text-warning-strong' : 'text-charcoal'}`}>
            {title} <span className="text-muted-foreground font-normal">({g[key].length})</span>
          </h3>
          {g[key].map((d) => (
            <DelegateCard
              key={d.id}
              d={d}
              capLabels={capLabels}
              deciding={decidingId === d.id}
              focused={focusedId === d.id}
              onRespond={onRespond}
              onOpen={onOpen}
            />
          ))}
        </section>
      ));
  })();
  return (
    <div className="space-y-3" data-testid="delegate-inbox">
      <h2 className="text-charcoal font-medium">Delegated to me</h2>
      {body}
    </div>
  );
};

export default DelegateInbox;
